import { useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { UserEntitiesResponse } from "@common/types/entityContext";
import type { Task } from "@common/types/task";
import { getUserEntities } from "@api/user.api";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";

/** Entity names for lane titles, and each entity's client for cross-client drops. */
export interface TaskEntityLookups {
    entityNamesById: ReadonlyMap<number, string>;
    entityWorkspaceIds: ReadonlyMap<number, string>;
}

const combineEntityLookups = (
    results: Array<UseQueryResult<UserEntitiesResponse | void>>,
): TaskEntityLookups => {
    const entities = results.flatMap(
        ({ data }) => data?.entities.map(({ entity }) => entity) ?? [],
    );
    return {
        entityNamesById: new Map(
            entities.map((entity) => [entity.id, entity.name ?? ""]),
        ),
        entityWorkspaceIds: new Map(
            entities.map((entity) => [entity.id, entity.workspaceId]),
        ),
    };
};

interface UseTaskEntityLookupsArgs {
    tasks: readonly Task[];
    isCrossWorkspace: boolean;
    /** Off unless lanes are by entity, since the organization page fetches per client. */
    isEnabled: boolean;
    /** The current workspace's entity names, on a workspace page. */
    entitiesNameMap: ReadonlyMap<number, string>;
}

/**
 * The entities lanes can show: the current workspace's on a workspace page,
 * each listed task's client's on the organization page, read from the same
 * cache the entity avatars fill.
 */
export const useTaskEntityLookups = ({
    tasks,
    isCrossWorkspace,
    isEnabled,
    entitiesNameMap,
}: UseTaskEntityLookupsArgs): TaskEntityLookups => {
    const workspaceIds = useMemo(
        () =>
            isEnabled && isCrossWorkspace
                ? [...new Set(tasks.map((task) => task.workspaceId))]
                : [],
        [isEnabled, isCrossWorkspace, tasks],
    );
    const clientLookups = useQueries({
        queries: workspaceIds.map((workspaceId) => ({
            queryKey: getEntitiesAccountsQueryKey(workspaceId),
            queryFn: () => getUserEntities(workspaceId),
            refetchOnMount: false,
        })),
        combine: combineEntityLookups,
    });
    const workspaceLookups = useMemo(
        () => ({
            entityNamesById: entitiesNameMap,
            entityWorkspaceIds: new Map<number, string>(),
        }),
        [entitiesNameMap],
    );
    return isCrossWorkspace ? clientLookups : workspaceLookups;
};
